import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Gauge, RotateCcw, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { SendResultsForm } from "./TaxCalculator";

const Intro = ({ onStart }) => (
  <div className="grid items-center gap-8 lg:grid-cols-[1.2fr_1fr]" data-testid="risk-intro">
    <div className="space-y-5">
      <p className="text-[#242020]/75">Dix questions simples pour mieux comprendre votre rapport au risque et à la volatilité des placements. Environ 3 minutes.</p>
      <ul className="grid gap-2 text-sm text-[#242020]/75 sm:grid-cols-3">
        {["Votre situation", "Vos réactions", "Vos préférences"].map((t, i) => (
          <li key={t} className="flex items-center gap-2 rounded-full border border-[#EDE3D9] bg-[#FFFDFB] px-4 py-2"><span className="font-display text-[#650D23]">{i + 1}</span>{t}</li>
        ))}
      </ul>
      <button onClick={onStart} className="btn-primary" data-testid="risk-start-button"><Sparkles className="h-4 w-4" />Commencer le questionnaire</button>
    </div>
    <div className="hidden justify-center lg:flex"><Dial index={2} muted /></div>
  </div>
);

const Dial = ({ index, muted = false }) => {
  const angle = -90 + index * 45;
  return (
    <svg viewBox="0 0 220 130" className="w-full max-w-[300px]" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => {
        const a0 = Math.PI * (1 - i / 5) - 0.03, a1 = Math.PI * (1 - (i + 1) / 5) + 0.03;
        const p = (a, r) => `${110 + r * Math.cos(a)} ${115 - r * Math.sin(a)}`;
        return <path key={i} d={`M${p(a0, 100)} A100 100 0 0 1 ${p(a1, 100)} L${p(a1, 70)} A70 70 0 0 0 ${p(a0, 70)} Z`}
          fill={muted ? "#EDE3D9" : i === index ? "#650D23" : i < index ? "#B98A8F" : "#EDE3D9"} />;
      })}
      <motion.g initial={{ rotate: -90 }} animate={{ rotate: angle }} transition={{ type: "spring", stiffness: 60, damping: 12 }} style={{ originX: "110px", originY: "115px" }}>
        <path d="M108 115 L110 30 L112 115 Z" fill="#4A0C1B" />
      </motion.g>
      <circle cx="110" cy="115" r="8" fill="#4A0C1B" />
    </svg>
  );
};

const QuestionStep = ({ q, i, total, value, onPick, onBack }) => (
  <motion.div key={q.id} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} data-testid={`risk-question-${q.id}`}>
    <div className="flex items-center justify-between text-xs text-[#242020]/60">
      <span className="uppercase tracking-widest text-[#650D23]">{q.theme}</span><span data-testid="risk-progress-label">Question {i + 1} sur {total}</span>
    </div>
    <div className="mt-3 flex gap-1.5">{Array.from({ length: total }).map((_, k) => <span key={k} className={`h-1.5 flex-1 rounded-full ${k <= i ? "bg-[#650D23]" : "bg-[#EDE3D9]"}`} />)}</div>
    <h3 className="mt-8 max-w-3xl text-2xl leading-snug sm:text-3xl">{q.q}</h3>
    <div className="mt-8 grid gap-3">
      {q.options.map((o, k) => (
        <button key={o} onClick={() => onPick(k)} data-testid={`risk-option-${q.id}-${k}`}
          className={`group flex items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-colors ${value === k ? "border-[#650D23] bg-[#650D23] text-[#FFFDFB]" : "border-[#EDE3D9] bg-[#FFFDFB] hover:border-[#650D23]"}`}>
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-display text-sm ${value === k ? "bg-[#FFFDFB] text-[#650D23]" : "bg-[#FAF6F1] text-[#650D23]"}`}>{String.fromCharCode(65 + k)}</span>
          <span>{o}</span>
        </button>
      ))}
    </div>
    {i > 0 && <button onClick={onBack} className="mt-6 inline-flex items-center gap-2 text-sm text-[#650D23]" data-testid="risk-back-button"><ArrowLeft className="h-4 w-4" />Question précédente</button>}
  </motion.div>
);

const Result = ({ res, answers, onReset }) => {
  const p = res.profile;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid gap-8 lg:grid-cols-2" data-testid="risk-result">
      <div className="rounded-2xl bg-[#650D23] p-8 text-center text-[#FFFDFB]">
        <p className="eyebrow !text-[#EDE3D9]">Votre profil d'investisseur</p>
        <div className="mx-auto mt-4 max-w-[280px] rounded-2xl bg-[#FFFDFB] p-4"><Dial index={res.index} /></div>
        <h3 className="mt-6 text-4xl uppercase tracking-wide" data-testid="risk-profile-name">{p.name}</h3>
        <p className="mt-1 text-sm text-[#EDE3D9]" data-testid="risk-score">Pointage : {res.score} / {res.max_score}</p>
        <p className="mx-auto mt-4 max-w-md text-[#EDE3D9]">{p.description}</p>
        {res.horizon_capped && <p className="mt-4 rounded-xl bg-[#FFFDFB]/10 p-3 text-xs" data-testid="risk-horizon-note">Comme vous aurez besoin de cet argent d'ici 2 ans, le profil Prudent est retenu, peu importe vos autres réponses.</p>}
        <div className="mt-6 text-left">
          <p className="text-xs uppercase tracking-widest text-[#EDE3D9]">Répartition indicative</p>
          <div className="mt-2 flex h-3 overflow-hidden rounded-full bg-[#FFFDFB]/20">
            {Object.entries(p.allocation).map(([k, v], i) => <span key={k} style={{ width: `${v}%` }} className={i ? "bg-[#EDE3D9]" : "bg-[#B98A8F]"} />)}
          </div>
          <div className="mt-2 flex justify-between text-xs text-[#EDE3D9]">{Object.entries(p.allocation).map(([k, v]) => <span key={k}>{k} : {v} %</span>)}</div>
        </div>
        <button onClick={onReset} className="mt-6 inline-flex items-center gap-2 text-sm text-[#EDE3D9] hover:text-[#FFFDFB]" data-testid="risk-reset-button"><RotateCcw className="h-4 w-4" />Recommencer</button>
      </div>
      <SendResultsForm endpoint="/risk-profile/send" payload={{ answers }} prefix="risk" />
    </motion.div>
  );
};

export const RiskProfile = () => {
  const [data, setData] = useState(null);
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState({});
  const [res, setRes] = useState(null);
  useEffect(() => {
    api.get("/risk-profile/questions").then((r) => setData(r.data));
  }, []);
  if (!data) return null;
  const qs = data.questions;

  const pick = async (k) => {
    const next = { ...answers, [qs[step].id]: k };
    setAnswers(next);
    if (step < qs.length - 1) { setTimeout(() => setStep(step + 1), 220); return; }
    const { data: r } = await api.post("/risk-profile/compute", { answers: next });
    setRes(r);
  };
  const reset = () => { setAnswers({}); setRes(null); setStep(0); };

  return (
    <section id="profil-investisseur" className="rounded-[2rem] border border-[#EDE3D9] bg-[#FAF6F1]/95 p-6 sm:p-10" data-testid="risk-profile">
      <div className="flex items-center gap-3"><span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#650D23] text-[#FFFDFB]"><Gauge className="h-5 w-5" /></span>
        <div><p className="eyebrow">Apprendre — Outil</p><h2 className="text-3xl">Quel est votre profil d'investisseur?</h2></div></div>
      <div className="mt-8">
        {res ? <Result res={res} answers={answers} onReset={reset} /> : step < 0 ? <Intro onStart={() => setStep(0)} /> : (
          <AnimatePresence mode="wait">
            <QuestionStep q={qs[step]} i={step} total={qs.length} value={answers[qs[step].id]} onPick={pick} onBack={() => setStep(step - 1)} />
          </AnimatePresence>
        )}
      </div>
      <p className="mt-8 text-xs leading-relaxed text-[#242020]/60" data-testid="risk-disclaimer">Outil éducatif seulement. Le profil obtenu et la répartition indicative ne constituent ni un conseil en placement ni une recommandation d'acheter un produit. Discutez-en avec un conseiller qualifié avant toute décision.</p>
    </section>
  );
};
